import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  Receipt,
  Wallet,
  AlertTriangle,
  TrendingUp,
} from 'lucide-react';
import { formatUsd } from '@/lib/paymentsData';

const Card4 = ({ label, value, caption, Icon, tone, active, onClick, testId }) => {
  const accent = {
    default: 'text-[hsl(var(--primary))]',
    success: 'text-[hsl(var(--success))]',
    warning: 'text-[hsl(var(--warning))]',
    danger: 'text-[hsl(var(--destructive))]',
  }[tone || 'default'];
  const bar = {
    default: 'bg-[hsl(var(--primary))]/60',
    success: 'bg-[hsl(var(--success))]/60',
    warning: 'bg-[hsl(var(--warning))]/60',
    danger: 'bg-[hsl(var(--destructive))]/60',
  }[tone || 'default'];
  const ring = {
    default: 'ring-[hsl(var(--primary))]/40',
    success: 'ring-[hsl(var(--success))]/40',
    warning: 'ring-[hsl(var(--warning))]/40',
    danger: 'ring-[hsl(var(--destructive))]/40',
  }[tone || 'default'];
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      className={cn(
        'relative overflow-hidden rounded-none border bg-[hsl(var(--card))] text-left transition-all',
        'hover:border-[hsl(var(--primary))]/30',
        active
          ? `border-[hsl(var(--primary))]/40 ring-2 ${ring}`
          : 'border-[hsl(var(--hairline))]'
      )}
    >
      <span aria-hidden className={cn('metallic absolute left-0 top-0 h-full w-[2px]', bar)} />
      <div className="p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <span className="text-[11px] leading-snug sm:text-sm lg:text-xs 2xl:text-sm font-bold uppercase tracking-[0.12em] break-words text-[hsl(var(--primary))]">
            {label}
          </span>
          {Icon ? <Icon size={16} className={cn('opacity-80', accent)} /> : null}
        </div>
        <div
          className="mt-1.5 text-lg sm:text-xl font-semibold tracking-tight tabular-nums text-foreground"
          data-testid={`${testId}-value`}
        >
          {value}
        </div>
        {caption ? (
          <div className="mt-1 text-xs text-muted-foreground">{caption}</div>
        ) : null}
      </div>
    </button>
  );
};

export const PaymentsStatsBar = ({ stats, view, onView }) => {
  const { t } = useTranslation();
  const {
    totalInvoiced = 0,
    collected = 0,
    outstanding = 0,
    pipeline = 0,
    overdue = 0,
    collectedPct = 0,
    counts = {},
  } = stats || {};
  return (
    <div
      className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5"
      data-testid="payments-stats"
    >
      <Card4
        label={t('payments.stats.collected')}
        value={formatUsd(collected)}
        caption={t('payments.stats.collectedCaption', { count: counts.paid || 0, pct: collectedPct })}
        Icon={Wallet}
        tone="success"
        active={view === 'collected'}
        onClick={() => onView('collected')}
        testId="pay-stat-collected"
      />
      <Card4
        label={t('payments.stats.outstanding')}
        value={formatUsd(outstanding)}
        caption={t('payments.stats.outstandingCaption', { count: counts.outstanding || 0 })}
        Icon={AlertTriangle}
        tone="warning"
        active={view === 'outstanding'}
        onClick={() => onView('outstanding')}
        testId="pay-stat-outstanding"
      />
      <Card4
        label={t('payments.stats.pipeline')}
        value={formatUsd(pipeline)}
        caption={t('payments.stats.pipelineCaption', { count: counts.jobs || 0 })}
        Icon={TrendingUp}
        tone="default"
        active={view === 'pipeline'}
        onClick={() => onView('pipeline')}
        testId="pay-stat-pipeline"
      />
      <Card4
        label={t('payments.stats.totalInvoiced')}
        value={formatUsd(totalInvoiced)}
        caption={t('payments.stats.totalCaption', {
          count: counts.total || 0,
          overdue: formatUsd(overdue),
        })}
        Icon={Receipt}
        tone={overdue > 0 ? 'danger' : 'default'}
        active={view === 'all'}
        onClick={() => onView('all')}
        testId="pay-stat-total"
      />
    </div>
  );
};

export default PaymentsStatsBar;
